import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { 
  Smile, Meh, Frown, Battery, BatteryLow, BatteryFull,
  Moon, Sun as SunIcon, Utensils, Pill, ArrowLeft, CheckCircle,
  TrendingUp, Calendar, AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MOOD_OPTIONS = [
  { value: 1, label: 'Very Low', icon: Frown, color: 'text-red-500' },
  { value: 2, label: 'Low', icon: Frown, color: 'text-orange-500' },
  { value: 3, label: 'Okay', icon: Meh, color: 'text-yellow-500' },
  { value: 4, label: 'Good', icon: Smile, color: 'text-green-400' },
  { value: 5, label: 'Great', icon: Smile, color: 'text-green-600' }
];

const SYMPTOM_OPTIONS = [
  'Headache', 'Fatigue', 'Nausea', 'Dizziness', 'Pain',
  'Shortness of breath', 'Fever', 'Cough', 'Anxiety', 'Other'
];

export default function DailyCheckIn() {
  const { getAuthHeader } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [existingCheckIn, setExistingCheckIn] = useState(null);
  const [history, setHistory] = useState([]);
  
  const [formData, setFormData] = useState({
    mood: 3,
    energy_level: 3,
    pain_level: 0,
    sleep_quality: 3,
    appetite: 'good',
    symptoms: [],
    notes: '',
    medications_taken: true
  });

  useEffect(() => {
    checkTodayStatus();
    fetchHistory();
  }, []);

  const checkTodayStatus = async () => {
    try {
      const response = await axios.get(`${API}/checkin/today`, getAuthHeader());
      if (response.data.has_checked_in) {
        setExistingCheckIn(response.data.checkin);
        setSubmitted(true);
      }
    } catch (error) {
      console.error('Error checking status:', error);
    }
  };

  const fetchHistory = async () => {
    try {
      const response = await axios.get(`${API}/checkin/history`, getAuthHeader());
      setHistory(response.data.slice(0, 7));
    } catch (error) {
      console.error('Error fetching history:', error);
    }
  };

  const toggleSymptom = (symptom) => {
    setFormData(prev => ({
      ...prev,
      symptoms: prev.symptoms.includes(symptom)
        ? prev.symptoms.filter(s => s !== symptom)
        : [...prev.symptoms, symptom]
    }));
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/checkin`, formData, getAuthHeader());
      toast.success('Check-in recorded! Your care team has been notified.');
      setSubmitted(true);
      fetchHistory();
    } catch (error) {
      toast.error('Failed to submit check-in');
    } finally {
      setLoading(false);
    }
  };

  const getMoodIcon = (value) => {
    const option = MOOD_OPTIONS.find(o => o.value === value);
    return option ? option.icon : Meh;
  };

  const getMoodColor = (value) => {
    if (value <= 2) return 'text-red-500';
    if (value === 3) return 'text-yellow-500';
    return 'text-green-500';
  };

  if (submitted) {
    const data = existingCheckIn || formData;
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-cn-sage-100 dark:from-cn-ink-900 dark:via-cn-ink-900 dark:to-cn-ink-800 py-8 px-4">
        <div className="max-w-2xl mx-auto">
          <button 
            onClick={() => navigate('/patient/dashboard')}
            className="flex items-center gap-2 text-slate-600 dark:text-slate-400 hover:text-cn-primary dark:hover:text-cn-primary mb-8"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Dashboard
          </button>

          <Card className="border-0 shadow-xl bg-white/80 dark:bg-cn-ink-800/80 backdrop-blur-xl mb-6">
            <CardContent className="p-8 text-center">
              <div className="w-20 h-20 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-10 h-10 text-green-600 dark:text-green-400" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900 dark:text-white font-display mb-2">
                Today's Check-in Complete
              </h2>
              <p className="text-slate-600 dark:text-slate-400 mb-6">
                Your care team will be notified of any concerns.
              </p>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-left">
                <div className="bg-slate-50 dark:bg-cn-ink-700/50 rounded-xl p-4">
                  <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">Mood</p>
                  <div className="flex items-center gap-2">
                    {React.createElement(getMoodIcon(data.mood), { className: `w-6 h-6 ${getMoodColor(data.mood)}` })}
                    <span className="font-semibold text-slate-900 dark:text-white">{data.mood}/5</span>
                  </div>
                </div>
                <div className="bg-slate-50 dark:bg-cn-ink-700/50 rounded-xl p-4">
                  <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">Pain</p>
                  <span className="font-semibold text-slate-900 dark:text-white">{data.pain_level}/10</span>
                </div>
                <div className="bg-slate-50 dark:bg-cn-ink-700/50 rounded-xl p-4">
                  <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">Energy</p>
                  <span className="font-semibold text-slate-900 dark:text-white">{data.energy_level}/5</span>
                </div>
                <div className="bg-slate-50 dark:bg-cn-ink-700/50 rounded-xl p-4">
                  <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">Sleep</p>
                  <span className="font-semibold text-slate-900 dark:text-white">{data.sleep_quality}/5</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* History */}
          <Card className="border-0 shadow-lg bg-white/80 dark:bg-cn-ink-800/80 backdrop-blur-xl">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 font-display">
                <TrendingUp className="w-5 h-5 text-cn-primary" />
                Recent Check-ins
              </CardTitle>
            </CardHeader>
            <CardContent>
              {history.length === 0 ? (
                <p className="text-slate-500 dark:text-slate-400 text-center py-4">No history yet</p>
              ) : (
                <div className="space-y-3">
                  {history.map((checkin, index) => (
                    <div key={checkin.id} className="flex items-center justify-between p-3 bg-slate-50 dark:bg-cn-ink-700/50 rounded-xl">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          checkin.mood >= 4 ? 'bg-green-100 dark:bg-green-900/30' :
                          checkin.mood === 3 ? 'bg-yellow-100 dark:bg-yellow-900/30' :
                          'bg-red-100 dark:bg-red-900/30'
                        }`}>
                          {React.createElement(getMoodIcon(checkin.mood), { 
                            className: `w-5 h-5 ${getMoodColor(checkin.mood)}` 
                          })}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-slate-900 dark:text-white">{checkin.date}</p>
                          <p className="text-xs text-slate-500 dark:text-slate-400">
                            Pain: {checkin.pain_level}/10 • Energy: {checkin.energy_level}/5
                          </p>
                        </div>
                      </div>
                      {checkin.pain_level >= 7 && (
                        <Badge className="bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400">
                          High Pain
                        </Badge>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-cn-sage-100 dark:from-cn-ink-900 dark:via-cn-ink-900 dark:to-cn-ink-800 py-8 px-4" data-testid="daily-checkin">
      <div className="max-w-2xl mx-auto">
        <button 
          onClick={() => navigate('/patient/dashboard')}
          className="flex items-center gap-2 text-slate-600 dark:text-slate-400 hover:text-cn-primary dark:hover:text-cn-primary mb-8"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Dashboard
        </button>

        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-cn-sage-100 dark:bg-cn-sage-900/30 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Calendar className="w-8 h-8 text-cn-primary dark:text-cn-primary" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white font-display">Daily Check-in</h1>
          <p className="text-slate-600 dark:text-slate-400">How are you feeling today?</p>
        </div>

        <Card className="border-0 shadow-xl bg-white/80 dark:bg-cn-ink-800/80 backdrop-blur-xl">
          <CardContent className="p-6 space-y-8">
            {/* Mood */}
            <div>
              <label className="text-sm font-medium text-slate-900 dark:text-white mb-4 block">How is your mood?</label>
              <div className="flex justify-between gap-2">
                {MOOD_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => setFormData({...formData, mood: option.value})}
                    className={`flex-1 flex flex-col items-center p-4 rounded-xl border-2 transition-all ${
                      formData.mood === option.value
                        ? 'border-cn-primary bg-cn-sage-50 dark:bg-cn-sage-900/30'
                        : 'border-slate-200 dark:border-cn-ink-700 hover:border-slate-300'
                    }`}
                  >
                    <option.icon className={`w-8 h-8 ${option.color}`} />
                    <span className="text-xs mt-2 text-slate-600 dark:text-slate-400">{option.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Pain Level */}
            <div>
              <label className="text-sm font-medium text-slate-900 dark:text-white mb-2 block">
                Pain Level: <span className="text-cn-primary dark:text-cn-primary">{formData.pain_level}/10</span>
              </label>
              <input
                type="range"
                min="0"
                max="10"
                value={formData.pain_level}
                onChange={(e) => setFormData({...formData, pain_level: parseInt(e.target.value)})}
                className="w-full h-2 bg-slate-200 dark:bg-cn-ink-700 rounded-lg appearance-none cursor-pointer accent-cn-primary"
              />
              <div className="flex justify-between text-xs text-slate-500 dark:text-slate-400 mt-1">
                <span>No pain</span>
                <span>Severe</span>
              </div>
            </div>

            {/* Energy Level */}
            <div>
              <label className="text-sm font-medium text-slate-900 dark:text-white mb-4 block">Energy Level</label>
              <div className="flex justify-between gap-2">
                {[1, 2, 3, 4, 5].map((level) => (
                  <button
                    key={level}
                    onClick={() => setFormData({...formData, energy_level: level})}
                    className={`flex-1 flex flex-col items-center p-3 rounded-xl border-2 transition-all ${
                      formData.energy_level === level
                        ? 'border-cn-primary bg-cn-sage-50 dark:bg-cn-sage-900/30'
                        : 'border-slate-200 dark:border-cn-ink-700 hover:border-slate-300'
                    }`}
                  >
                    {level <= 2 ? <BatteryLow className="w-6 h-6 text-red-500" /> :
                     level === 3 ? <Battery className="w-6 h-6 text-yellow-500" /> :
                     <BatteryFull className="w-6 h-6 text-green-500" />}
                    <span className="text-xs mt-1 text-slate-600 dark:text-slate-400">{level}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Sleep Quality */}
            <div>
              <label className="text-sm font-medium text-slate-900 dark:text-white mb-4 block">
                <Moon className="w-4 h-4 inline mr-2" />
                Sleep Quality
              </label>
              <div className="flex justify-between gap-2">
                {['Poor', 'Fair', 'Okay', 'Good', 'Great'].map((label, index) => (
                  <button
                    key={index}
                    onClick={() => setFormData({...formData, sleep_quality: index + 1})}
                    className={`flex-1 p-3 rounded-xl border-2 text-center transition-all ${
                      formData.sleep_quality === index + 1
                        ? 'border-cn-primary bg-cn-sage-50 dark:bg-cn-sage-900/30'
                        : 'border-slate-200 dark:border-cn-ink-700 hover:border-slate-300'
                    }`}
                  >
                    <span className="text-sm text-slate-700 dark:text-slate-300">{label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Appetite */}
            <div>
              <label className="text-sm font-medium text-slate-900 dark:text-white mb-4 block">
                <Utensils className="w-4 h-4 inline mr-2" />
                Appetite
              </label>
              <div className="flex gap-2">
                {['poor', 'fair', 'good', 'excellent'].map((level) => (
                  <button
                    key={level}
                    onClick={() => setFormData({...formData, appetite: level})}
                    className={`flex-1 p-3 rounded-xl border-2 capitalize transition-all ${
                      formData.appetite === level
                        ? 'border-cn-primary bg-cn-sage-50 dark:bg-cn-sage-900/30'
                        : 'border-slate-200 dark:border-cn-ink-700 hover:border-slate-300'
                    }`}
                  >
                    {level}
                  </button>
                ))}
              </div>
            </div>

            {/* Medications */}
            <div className="flex items-center justify-between p-4 bg-slate-50 dark:bg-cn-ink-700/50 rounded-xl">
              <div className="flex items-center gap-3">
                <Pill className="w-5 h-5 text-cn-accent" />
                <span className="font-medium text-slate-900 dark:text-white">Medications taken as prescribed?</span>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setFormData({...formData, medications_taken: true})}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                    formData.medications_taken
                      ? 'bg-green-500 text-white'
                      : 'bg-slate-200 dark:bg-cn-ink-600 text-slate-700 dark:text-slate-300'
                  }`}
                >
                  Yes
                </button>
                <button
                  onClick={() => setFormData({...formData, medications_taken: false})}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                    !formData.medications_taken
                      ? 'bg-red-500 text-white'
                      : 'bg-slate-200 dark:bg-cn-ink-600 text-slate-700 dark:text-slate-300'
                  }`}
                >
                  No
                </button>
              </div>
            </div>

            {/* Symptoms */}
            <div>
              <label className="text-sm font-medium text-slate-900 dark:text-white mb-3 block">Any symptoms today?</label>
              <div className="flex flex-wrap gap-2">
                {SYMPTOM_OPTIONS.map((symptom) => (
                  <button
                    key={symptom}
                    onClick={() => toggleSymptom(symptom)}
                    className={`px-3 py-2 rounded-full text-sm transition-all ${
                      formData.symptoms.includes(symptom)
                        ? 'bg-orange-500 text-white'
                        : 'bg-slate-100 dark:bg-cn-ink-700 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-cn-ink-600'
                    }`}
                  >
                    {symptom}
                  </button>
                ))}
              </div>
            </div>

            {/* Notes */}
            <div>
              <label className="text-sm font-medium text-slate-900 dark:text-white mb-2 block">Additional notes (optional)</label>
              <Textarea
                placeholder="Anything else you'd like to share..."
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                className="bg-slate-50 dark:bg-cn-ink-700/50"
              />
            </div>

            {/* Warning */}
            {(formData.mood <= 2 || formData.pain_level >= 7) && (
              <div className="flex items-center gap-3 p-4 bg-orange-50 dark:bg-orange-900/20 rounded-xl border border-orange-200 dark:border-orange-800">
                <AlertTriangle className="w-5 h-5 text-orange-600 dark:text-orange-400 flex-shrink-0" />
                <p className="text-sm text-orange-700 dark:text-orange-300">
                  Your care team will be notified about your current status.
                </p>
              </div>
            )}

            {/* Submit */}
            <Button
              onClick={handleSubmit}
              disabled={loading}
              data-testid="submit-checkin-btn"
              className="w-full bg-gradient-to-br from-cn-primary to-cn-sage-500 hover:from-cn-primary hover:to-cn-sage-600 text-white rounded-xl h-12"
            >
              {loading ? (
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Submitting...
                </div>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Complete Check-in
                </>
              )}
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
